/**
 * <park-button> — pill button, the reference implementation of the ISOMORPHIC
 * park-* contract (epic #138, issue #141; see docs/design-system-rebuild-plan.md).
 *
 * Single source of truth = the pure `renderParkButton()` function below. It is
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkButton.astro`,
 *     which calls it server-side and emits the markup inside <park-button data-ssr>
 *     so the button is in the initial HTML (SEO / no-JS), and
 *   - the Claude-Design preview, where the custom element renders it client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * Emits the shared `.park-btn(+--accent/--ghost/--hero-ghost)` classes (in
 * park.css since #189) — no per-component CSS.
 *
 * Attributes:
 *   - variant="primary|accent|ghost|hero-ghost"  (default "accent")
 *   - href="…"     when present, renders an <a>; otherwise a <button>.
 *   - disabled     disables the control.
 *   - type="…"     forwarded to the <button> (default "button"; href form ignores).
 *   - target/rel="…"  forwarded to the <a> form.
 * Slotted content = the label.
 *
 * @dsSpec design-system#FR-3 — the reusable pill CTA every page composes instead of restyling a link.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-button";

export interface ParkButtonProps {
	variant?: string;
	href?: string;
	disabled?: boolean;
	type?: string;
	target?: string;
	rel?: string;
}

/** variant → utility class list (the styling contract, tied to park.css). */
function variantClass(variant: string): string {
	switch (variant) {
		case "accent":
			return "park-btn park-btn--accent";
		case "ghost":
			return "park-btn park-btn--ghost";
		case "hero-ghost":
			return "park-btn park-btn--hero-ghost";
		default:
			return "park-btn";
	}
}

/**
 * Pure render: (props, label HTML) → inner HTML string. No DOM access, so it is
 * safe to call server-side (the .astro wrapper) and client-side (the element).
 * `label` is trusted author markup (already escaped/rendered by the caller).
 */
export function renderParkButton(
	props: ParkButtonProps,
	label: string,
): string {
	// `|| default`, not `?? default`: the custom element reads a MISSING attribute
	// as "" (empty string, not undefined), so `??` would leave the documented
	// default un-applied client-side and the element would render differently from
	// the SSR wrapper (which gets `undefined` from Astro props). Treating "" as the
	// default keeps the render isomorphic — identical live vs standalone (#139).
	const cls = variantClass(props.variant || "accent");
	const href = props.href ?? "";
	if (href) {
		if (props.disabled) {
			return html`<a class="${cls} is-disabled" role="link" aria-disabled="true">${label}</a>`;
		}
		const target = props.target ? html` target="${esc(props.target)}"` : "";
		const rel = props.rel ? html` rel="${esc(props.rel)}"` : "";
		return html`<a class="${cls}" href="${esc(href)}"${target}${rel}>${label}</a>`;
	}
	const type = esc(props.type || "button"); // "" (absent attr) → default, like variant above
	const dis = props.disabled ? " disabled" : "";
	return html`<button class="${cls}" type="${type}"${dis}>${label}</button>`;
}

class ParkButton extends ParkElement {
	static get observedAttributes(): string[] {
		return ["variant", "href", "disabled", "type", "target", "rel"];
	}

	protected render(): string {
		return renderParkButton(
			{
				variant: this.attr("variant"),
				href: this.attr("href"),
				disabled: this.boolAttr("disabled"),
				type: this.attr("type"),
				target: this.attr("target"),
				rel: this.attr("rel"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkButton);

export { ParkButton };
